import { siteConfig } from "@/config/site";

// Stable JSON-LD `@id` for the publishing Organization. Used as the
// single canonical Organization node; every other schema (Article,
// WebSite, SoftwareApplication, Product, Person) cross-references it
// via `{ "@id": ORGANIZATION_ID }` instead of inlining a duplicate
// `{ "@type": "Organization", name: ... }` blob. This consolidates the
// schema graph so Google sees one publisher, not 4-7 unrelated nodes.
const ORGANIZATION_ID = `${siteConfig.url}/#organization`;
const WEBSITE_ID = `${siteConfig.url}/#website`;

export const ORGANIZATION_REF = { "@id": ORGANIZATION_ID } as const;
// The Faxify MCP server's entity id. It is minted by faxify-mcp (its WebAPI
// node is `${siteConfig.mcpUrl}/#webapi`); /mcp here reuses it so both sites
// describe ONE WebAPI entity. Keep the two in sync.
export const MCP_WEBAPI_ID = "https://mcp.faxify.com/#webapi";
// Canonical `@id` for the Faxify consumer app entity. The app is surfaced on
// two domains (this marketing site and app.faxify.com); giving both
// SoftwareApplication nodes the SAME `@id` collapses them into one rated
// entity in Google's graph instead of two competing "Faxify" apps. Keep this
// string identical in faxify-web-app. The MCP server is deliberately NOT this
// entity — it's a `WebAPI` (see that case) and never carries this rating.
const SOFTWARE_ID = `${siteConfig.url}/#software-faxify`;

// Real, verifiable combined app-store rating (Apple US + Apple Canada +
// Google Play) as of 2026-09-21. Shared by the SoftwareApplication node and
// the per-tier pricing `Product` nodes so the two never drift. Google's
// Product rich result requires a Product carrying `offers` to also include
// `aggregateRating` (or `review`); without it Semrush/Google flag a markup
// error. Refresh quarterly or on a major store-listing change — never
// fabricate (Google penalizes schema-spam). Numbers must be Numbers.
//
// Live-verified 2026-09-21 from the public store listings:
//   Apple US     769 ratings @ 4.75683  (itunes.apple.com/lookup?id=6444380905)
//   Apple Canada 188 ratings @ 4.59042  (same lookup, &country=ca)
//   Google Play  299 ratings @ 4.82     (listing JSON-LD aggregateRating)
//   Combined    1256 ratings, weighted mean 4.7470 -> 4.7
// Previous snapshots: 4.7 / 1069 (2026-07-29), 4.6 / 975 (2026-05-15).
export const FAXIFY_AGGREGATE_RATING = {
  "@type": "AggregateRating",
  ratingValue: 4.7,
  ratingCount: 1256,
  bestRating: 5,
  worstRating: 1,
} as const;

export interface StructuredDataProps {
  type?:
    | "Organization"
    | "WebSite"
    | "Article"
    | "BreadcrumbList"
    | "Person"
    | "FAQPage"
    | "HowTo"
    | "Product"
    | "Service"
    | "WebAPI"
    | "Review"
    | "Event"
    | "SoftwareApplication"
    | "AboutPage"
    | "CollectionPage"
    | "VideoObject";
  data?: Record<string, any>;
}

// Article callers may pass `author` and `image` as either a plain string
// (we'll wrap) or a fully-formed schema object (we pass through). Same
// for `publisher` and `mainEntityOfPage`. The discriminated unions below
// keep the wrapping logic explicit.
interface ArticleData {
  headline: string;
  description: string;
  author?: string | Record<string, unknown>;
  datePublished?: string;
  dateModified?: string;
  image?: string | Record<string, unknown>;
  publisher?: Record<string, unknown>;
  url?: string;
  articleSection?: string;
  wordCount?: number;
  keywords?: string[];
  mainEntityOfPage?: Record<string, unknown>;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface HowToStepItem {
  name: string;
  text: string;
}

export function StructuredData({
  type = "Organization",
  data = {},
}: StructuredDataProps) {
  const getStructuredData = () => {
    const baseData = {
      "@context": "https://schema.org",
    };

    switch (type) {
      case "Organization":
        return {
          ...baseData,
          "@type": "Organization",
          "@id": ORGANIZATION_ID,
          name: siteConfig.companyName,
          alternateName: "Faxify",
          url: siteConfig.url,
          logo: `${siteConfig.url}/logo.png`,
          image: `${siteConfig.url}/og-image.png`,
          description: siteConfig.description,
          email: siteConfig.company.email,
          telephone: siteConfig.company.phone,
          address: {
            "@type": "PostalAddress",
            streetAddress: siteConfig.company.address,
            addressLocality: siteConfig.company.addressLocality,
            addressRegion: siteConfig.company.addressRegion,
            postalCode: siteConfig.company.postalCode,
            addressCountry: "US",
          },
          sameAs: siteConfig.socials
            .map(s => s.href)
            .filter((href): href is string => Boolean(href)),
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            telephone: siteConfig.company.phone,
            email: siteConfig.company.email,
            areaServed: ["US", "CA"],
            availableLanguage: ["en", "es", "fr"],
            url: siteConfig.url,
          },
          foundingDate: "2021-11",
          ...data,
        };

      case "WebSite":
        // No `potentialAction.SearchAction` until a working /search route
        // exists. Google's SiteLinks SearchBox spec requires the endpoint
        // to actually respond — declaring it without an implementation is
        // a Rich Results validation error. See SEO.md §20.
        return {
          ...baseData,
          "@type": "WebSite",
          "@id": WEBSITE_ID,
          // The site's NAME is the brand ("Faxify"), not the long homepage
          // <title>. Google uses this (with og:site_name) for the SERP site
          // name, so it must be the short brand.
          name: siteConfig.shortName,
          url: siteConfig.url,
          description: siteConfig.description,
          publisher: ORGANIZATION_REF,
          ...data,
        };

      case "Article": {
        const articleData = data as ArticleData;

        // Author: wrap a bare string as Person; pass through if the caller
        // already provided a fully-typed schema node.
        const author =
          typeof articleData.author === "string" || !articleData.author
            ? {
                "@type": "Person",
                name: (articleData.author as string) || siteConfig.author,
              }
            : articleData.author;

        // Image: wrap a bare URL as ImageObject; pass through schema nodes;
        // omit if absent (Google Rich Results requires a valid Article image,
        // so callers should always supply one for blog posts).
        const image =
          typeof articleData.image === "string"
            ? { "@type": "ImageObject", url: articleData.image }
            : articleData.image;

        return {
          ...baseData,
          "@type": "Article",
          headline: articleData.headline,
          description: articleData.description,
          author,
          // Google's Article Rich Results validator requires
          // `publisher.logo` inline — it doesn't reliably resolve
          // @id-only references for that field. We include the @id
          // so the node still merges into the canonical Organization
          // graph; the inline `@type` + `name` + `logo` satisfy the
          // strict validator path.
          publisher: articleData.publisher ?? {
            "@type": "Organization",
            "@id": ORGANIZATION_ID,
            name: siteConfig.companyName,
            logo: {
              "@type": "ImageObject",
              url: `${siteConfig.url}/logo.png`,
            },
          },
          datePublished: articleData.datePublished,
          dateModified: articleData.dateModified || articleData.datePublished,
          image,
          url: articleData.url,
          articleSection: articleData.articleSection || "Technology",
          wordCount: articleData.wordCount,
          keywords: articleData.keywords,
          mainEntityOfPage: articleData.mainEntityOfPage ?? {
            "@type": "WebPage",
            "@id": articleData.url,
          },
        };
      }

      case "BreadcrumbList":
        return {
          ...baseData,
          "@type": "BreadcrumbList",
          ...data,
        };

      case "Person":
        return {
          ...baseData,
          "@type": "Person",
          name: data.name || siteConfig.author,
          jobTitle: data.jobTitle || "Software Developer",
          worksFor: ORGANIZATION_REF,
          url: data.url || siteConfig.url,
          sameAs:
            data.sameAs ||
            [siteConfig.socials[0]?.href, siteConfig.socials[1]?.href].filter(
              Boolean
            ),
          ...data,
        };

      case "FAQPage": {
        // `faqs` is an internal carrier shape — Schema.org's FAQPage
        // vocabulary only knows `mainEntity` (an array of `Question`
        // nodes). Pull it out of the rest spread so the raw `faqs`
        // property doesn't leak into the emitted JSON-LD and trip
        // structured-data validators (e.g. Semrush "property faqs is
        // not recognized by Schema.org vocabulary").
        const { faqs, ...rest } = data;

        return {
          ...baseData,
          "@type": "FAQPage",
          mainEntity: (faqs || []).map((faq: FAQItem) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
          ...rest,
        };
      }

      case "HowTo": {
        // `steps` is an internal carrier shape, the same pattern as `faqs`
        // above — Schema.org's HowTo vocabulary knows `step` (an array of
        // `HowToStep` nodes), so pull the raw `steps` key out of the spread
        // instead of leaking it into the emitted JSON-LD.
        //
        // Note on value: Google retired HowTo rich results in 2023, so this
        // node earns no SERP feature. It is emitted for answer engines and
        // other structured-data consumers, which still read procedure steps
        // when deciding what to quote.
        const { steps, ...rest } = data;

        return {
          ...baseData,
          "@type": "HowTo",
          step: (steps || []).map((item: HowToStepItem, index: number) => ({
            "@type": "HowToStep",
            position: index + 1,
            name: item.name,
            text: item.text,
          })),
          ...rest,
        };
      }

      case "Product":
        // `brand` stays as a `Brand` node (different type than
        // Organization — Google's Product validator treats them
        // separately). `manufacturer` references the canonical
        // Organization via @id so the graph stays consolidated.
        return {
          ...baseData,
          "@type": "Product",
          name: data.name || siteConfig.name,
          description: data.description || siteConfig.description,
          brand: {
            "@type": "Brand",
            // The BRAND is Faxify. `companyName` is the legal entity
            // ("Const Agility, LLC"), which is what `manufacturer` already
            // points at via the Organization node — a buyer searching the
            // brand searches "Faxify".
            name: siteConfig.name.split(":")[0],
          },
          manufacturer: ORGANIZATION_REF,
          category: data.category || "Software",
          // `image` is REQUIRED for Google merchant listings (Semrush flagged
          // every /pricing Product as invalid without it, 2026-10-10). The
          // plans and passes have no product photo, so use the 1:1 brand logo.
          // Not og-image.png: its "Truly free online fax" headline would sit
          // beside a paid plan's price, and Google wants the image to
          // represent the marked-up item.
          image: `${siteConfig.url}/logo.png`,
          // DELIBERATELY NO `aggregateRating` HERE. The 4.7/1256 is the app
          // store's rating of the MOBILE APP, and it lives on the
          // SoftwareApplication node alone. Attaching it to each pricing plan
          // asserts that "Faxify Basic Plan" was rated 4.7 by 1,256 people,
          // which nobody ever rated — Google requires an aggregateRating to be
          // about the item it is attached to, and misattributed ratings are a
          // structured-data manual-action risk.
          //
          // The previous comment here justified it as satisfying a "missing
          // field aggregateRating" flag. That flag is a WARNING on a
          // recommended property: a Product carrying `offers` is already valid
          // without it. A warning is the correct trade for not fabricating a
          // per-plan rating.
          ...data,
        };

      case "Service":
        return {
          ...baseData,
          "@type": "Service",
          ...data,
        };

      case "Review":
        return {
          ...baseData,
          "@type": "Review",
          ...data,
        };

      case "Event":
        return {
          ...baseData,
          "@type": "Event",
          ...data,
        };

      case "AboutPage":
        // Marks a WebPage as the canonical "about" page for an entity.
        // Defaults wire `isPartOf` to the canonical WebSite node and
        // `mainEntity` to the canonical Organization node so Google sees
        // /about as Const Agility's entity home page rather than as a
        // freestanding WebPage. Callers can override any field via `data`.
        return {
          ...baseData,
          "@type": "AboutPage",
          name: data.name,
          description: data.description,
          url: data.url,
          inLanguage: data.inLanguage,
          isPartOf: { "@id": WEBSITE_ID },
          mainEntity: ORGANIZATION_REF,
          ...data,
        };

      case "CollectionPage":
        // Marks a listing page (e.g. /blogs) as a collection of items.
        // `isPartOf` wires it to the canonical WebSite node so Google sees
        // the listing as part of the site graph, not a freestanding page.
        // The caller supplies `mainEntity` as an `ItemList` of the listed
        // items. We deliberately do NOT inline full `BlogPosting` nodes here
        // — the canonical `Article` node for each post lives on that post's
        // own detail page, so each `ListItem` carries only position + url +
        // name to avoid duplicating (and diluting) the per-post schema.
        return {
          ...baseData,
          "@type": "CollectionPage",
          name: data.name,
          description: data.description,
          url: data.url,
          inLanguage: data.inLanguage,
          isPartOf: { "@id": WEBSITE_ID },
          ...data,
        };

      case "SoftwareApplication": {
        // Faxify is a SaaS — this is the right schema type, not LocalBusiness.
        // `aggregateRating` is the real, verifiable combined rating across
        // the Apple US, Apple Canada, and Google Play store listings as of
        // 2026-05-15 (sourced via WebFetch on each store page; see notes in
        // the Phase 1 commit history). Refresh quarterly or when a major
        // store-listing update lands; faking ratings here would trigger
        // Google's schema-spam penalties.
        //
        // Notes on schema validity:
        // - `operatingSystem` must be a Text value or an array of Text. Joining
        //   the OS list with a comma in a single string is non-idiomatic and
        //   trips Semrush's structured-data check; emit an array instead.
        // - `availableOnDevice` is NOT a valid property of SoftwareApplication
        //   (it belongs on Product), so it is intentionally omitted here.
        // - `aggregateRating` numeric fields must be Numbers, not Strings.
        const appData = data as any;
        // `offerDescription` is an internal carrier for `offers.description`
        // (below). Strip it from the rest-spread so it never leaks to the top
        // level as an unrecognized schema.org property (Semrush flags it).
        const { offerDescription, ...rest } = appData;

        return {
          ...baseData,
          "@type": "SoftwareApplication",
          "@id": SOFTWARE_ID,
          name: appData.name || "Faxify",
          alternateName: appData.alternateName || siteConfig.name,
          description: appData.description || siteConfig.description,
          url: appData.url || siteConfig.url,
          image: appData.image || `${siteConfig.url}/og-image.png`,
          applicationCategory:
            appData.applicationCategory || "BusinessApplication",
          applicationSubCategory: appData.applicationSubCategory || "Fax",
          operatingSystem: appData.operatingSystem || [
            "iOS",
            "Android",
            "Web",
            "macOS",
            "Windows",
          ],
          offers: {
            "@type": "Offer",
            price: appData.price || "0",
            priceCurrency: appData.priceCurrency || "USD",
            description:
              offerDescription ||
              "Free tier: 5 pages per month. Paid plans from $7.99/month.",
          },
          // schema.org: releaseDate is a Product property; a SoftwareApplication
          // (a CreativeWork) carries its release as datePublished — the App Store
          // release date.
          datePublished: "2023-01-08",
          aggregateRating: FAXIFY_AGGREGATE_RATING,
          provider: ORGANIZATION_REF,
          inLanguage: ["en", "es", "fr"],
          // `...rest` (data minus offerDescription) spreads last so callers can
          // still override any field if a page ever needs to.
          ...rest,
        };
      }

      case "WebAPI": {
        // Faxify MCP (the Model Context Protocol server) is an HTTP API that
        // lets AI assistants drive Faxify — a `WebAPI` (subtype of `Service`),
        // NOT a `SoftwareApplication`. Typing it as a Software App made
        // validators demand an `aggregateRating`/`review`; the MCP server has
        // no rating of its own and must not borrow the consumer app's
        // app-store rating (that would misattribute reviews to a different
        // product). `WebAPI` carries no rating requirement, so this is both
        // accurate and error-free. `offerDescription` is the internal carrier
        // for `offers.description` (stripped from the rest-spread).
        const apiData = data as any;
        const { offerDescription, ...rest } = apiData;

        return {
          ...baseData,
          "@type": "WebAPI",
          name: apiData.name || "Faxify MCP",
          description: apiData.description || siteConfig.description,
          url: apiData.url || siteConfig.url,
          offers: {
            "@type": "Offer",
            price: apiData.price || "0",
            priceCurrency: apiData.priceCurrency || "USD",
            description: offerDescription || undefined,
          },
          provider: ORGANIZATION_REF,
          // No inLanguage: WebAPI is a schema.org Service, and inLanguage is not
          // valid on a Service (Semrush flagged it as a markup error).
          ...rest,
        };
      }

      case "VideoObject":
        // Video rich-result node for a hosted YouTube video (the /mcp hero
        // demo). Google requires `name`, `thumbnailUrl`, and `uploadDate`;
        // `description` + `embedUrl` are strongly recommended. `duration` is
        // intentionally omitted when the real value isn't known rather than
        // guessed (it's recommended, not required). `publisher` is inlined
        // with a logo — like Article — because Google's video validator does
        // not reliably resolve @id-only publisher references.
        return {
          ...baseData,
          "@type": "VideoObject",
          name: data.name,
          description: data.description,
          thumbnailUrl: data.thumbnailUrl,
          uploadDate: data.uploadDate,
          contentUrl: data.contentUrl,
          embedUrl: data.embedUrl,
          publisher: data.publisher ?? {
            "@type": "Organization",
            "@id": ORGANIZATION_ID,
            name: siteConfig.companyName,
            logo: {
              "@type": "ImageObject",
              url: `${siteConfig.url}/logo.png`,
            },
          },
          ...data,
        };

      default:
        return baseData;
    }
  };

  const structuredData = getStructuredData();

  // `suppressHydrationWarning` is the React-recommended fix for JSON-LD
  // `<script dangerouslySetInnerHTML>` blocks under Next.js streaming SSR.
  // Next.js streams an initial empty placeholder for this script, then
  // flushes the JSON.stringify result; React's hydration check sees that
  // initial empty `__html: ""` vs. the client's final populated value and
  // logs a dev-mode mismatch. The served HTML in production is fully
  // populated either way (verified via `curl` on prod), so this is purely
  // a dev-mode console warning. `suppressHydrationWarning` tells React
  // not to compare; the schema is identical between server and client.
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData),
      }}
      suppressHydrationWarning
      type="application/ld+json"
    />
  );
}

// Helper function to generate breadcrumb structured data.
//
// Per Google's BreadcrumbList guidance (updated late 2024), the final list
// item should omit the `item` URL field — it represents the current page,
// which is implicit. Including it on the last entry is flagged as a markup
// warning by Semrush and Google Rich Results.
export function generateBreadcrumbData(
  items: Array<{ name: string; url: string }>
) {
  return {
    itemListElement: items.map((item, index) => {
      const isLast = index === items.length - 1;
      const element: Record<string, unknown> = {
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
      };

      if (!isLast) {
        element.item = item.url;
      }

      return element;
    }),
  };
}
